import { useCallback } from 'react'
import { useIntegrations } from '../views/Integration/useIntegrations'
import { selectAccountId } from '../store/auth/authSlice'
import { useAppSelector } from '../store/hooks'
import { useGetMeQuery } from '../store/user/user'
import { IntegrationRequirementResult, INTEGRATIONS_REQUIRING_USER_CONNECTION } from './integrationRequirement'
import { IntegrationServiceId } from './integrations'
import { useIsZoomExempt } from './useIsZoomExempt'

/**
 * Returns a checker that evaluates whether the current user satisfies the requirements to use a given integration provider.
 *
 * Requirement rules:
 * 1. Exempt accounts bypass all checks (always satisfied)
 * 2. Account must allow the integration (enterprise config)
 * 3. If the provider requires a user-level connection (Zoom, Webex):
 *    - User must have a 'live' integration
 */
export const useIntegrationRequirement = () => {
  const accountId = useAppSelector(selectAccountId)
  const { isIntegrationAllowed } = useIntegrations()
  const isZoomExempt = useIsZoomExempt()
  const { data: currentUser } = useGetMeQuery({ accountId })

  return useCallback(
    (serviceId: IntegrationServiceId): IntegrationRequirementResult => {
      const accountAllowed = isIntegrationAllowed(serviceId)
      if (serviceId === IntegrationServiceId.ZOOM && isZoomExempt()) {
        return { satisfied: true, accountAllowed }
      }

      const hasLiveIntegration = currentUser?.integrations?.some((i) => i.serviceId === serviceId && i.state === 'live') ?? false

      if (hasLiveIntegration || !INTEGRATIONS_REQUIRING_USER_CONNECTION.has(serviceId)) {
        return { satisfied: true, accountAllowed }
      }

      return { satisfied: hasLiveIntegration, accountAllowed }
    },
    [isIntegrationAllowed, isZoomExempt, currentUser],
  )
}
